const API_BASE_URL = '/api/contacts';

export interface ContactCampaign {
  id: string;
  name: string;
}

export interface Contact {
  id: number;
  /**
   * Set when the row comes from a campaign, where `id` is the campaign link
   * and this is the contacts row. Contact-scoped actions must use it.
   */
  contactId?: number;
  /** Last conclusive check of the address; null when never checked. */
  emailCheckedAt?: string | null;
  /** Outcome of the last live verify attempt, inconclusive ones included. */
  emailCheckStatus?: string | null;
  workspaceId: number;
  prospectExternalId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  companyNumber: string | null;
  companyWebsite: string | null;
  industry: string | null;
  linkedinUrl: string | null;
  profileImageUrl: string | null;
  location: string | null;
  /** Provider confidence 0-100, or null when nothing ever scored this address. */
  enrichmentConfidence: number | null;
  /** 'active' until falcon-mailer reports a bounce, then 'bounced'. */
  emailStatus: string | null;
  enrichedAt: string;
  createdAt: string;
  updatedAt: string;
  campaigns?: ContactCampaign[];
}

export interface EmailVerification {
  email: string;
  status: 'valid' | 'invalid' | 'catch-all' | 'unknown' | 'spamtrap' | 'abuse' | 'do_not_mail';
  subStatus: string;
  freeEmail: boolean;
  isRoleBased: boolean;
  isCatchAll: boolean;
  isDisposable: boolean;
  confidence: number;
  smtpProvider: string;
  mxRecords: string[];
}

export interface VerifyEmailResult {
  contact: Contact;
  verification: EmailVerification;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ContactsResponse {
  contacts: Contact[];
  pagination: PaginationInfo;
}

export interface ContactsFilters {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'createdAt' | 'email' | 'company' | 'name';
  sortOrder?: 'asc' | 'desc';
}

export class ContactsService {
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async getContacts(filters: ContactsFilters = {}): Promise<ContactsResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const query = params.toString();
    return this.makeRequest<ContactsResponse>(query ? `?${query}` : '');
  }

  async getContact(id: number): Promise<Contact> {
    return this.makeRequest<Contact>(`/${id}`);
  }

  async deleteContact(id: number): Promise<{ success: boolean; deleted: number }> {
    return this.makeRequest(`/${id}`, { method: 'DELETE' });
  }

  async updateContact(id: number, data: Partial<Pick<Contact, 'firstName' | 'lastName' | 'email' | 'title' | 'company' | 'companyWebsite' | 'location'>>): Promise<Contact> {
    return this.makeRequest<Contact>(`/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async verifyContactEmail(id: number): Promise<VerifyEmailResult> {
    return this.makeRequest<VerifyEmailResult>(`/${id}/verify`, { method: 'POST' });
  }

  async bulkDeleteContacts(ids: number[]): Promise<{ success: boolean; deleted: number }> {
    return this.makeRequest('/', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }

  async addProspectsToContacts(prospectIds: string[]): Promise<{ success: boolean; added: number; message?: string }> {
    return this.makeRequest('/', {
      method: 'POST',
      body: JSON.stringify({ prospectIds }),
    });
  }
}

export const contactsService = new ContactsService();
